/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperBasketsV2 } from '@/scapi';
import { CYBERSOURCE_ROUTES } from './routes';
import { storePaypalReadyBasket, type PaypalCorrelation } from './paypal-session';
import type { PaypalCheckoutDestination } from './paypal-checkout';
import { getOrCreateCheckoutCorrelationId } from '@/lib/checkout/correlation';

export interface PaypalConfirmation {
    basketId: string;
    basket?: ShopperBasketsV2.schemas['Basket'];
    destination?: PaypalCheckoutDestination;
}

export async function confirmPaypalApproval({
    payerId,
    token,
    correlation,
    signal,
}: {
    payerId: string;
    token: string;
    correlation: PaypalCorrelation;
    signal?: AbortSignal;
}): Promise<PaypalConfirmation> {
    if (!token || token !== correlation.checkoutToken) {
        throw new Error('PayPal session token mismatch');
    }

    const correlationId = getOrCreateCheckoutCorrelationId();
    const response = await fetch(CYBERSOURCE_ROUTES.paypalReturn, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-correlation-id': correlationId },
        body: JSON.stringify({
            payerId,
            sessionsRequestId: correlation.sessionsRequestId,
            sessionProcessorTid: correlation.sessionProcessorTid,
        }),
        signal,
    });
    const body = (await response.json().catch(() => ({}))) as {
        success?: boolean;
        basketId?: string;
        basket?: ShopperBasketsV2.schemas['Basket'];
        destination?: PaypalCheckoutDestination;
        error?: { code?: string };
    };
    if (!response.ok || !body.success || !body.basketId || !body.basket || !body.destination) {
        throw new Error(`paypal-return failed (status=${response.status}, code=${body.error?.code ?? 'none'})`);
    }

    storePaypalReadyBasket(body.basketId, body.destination);
    return { basketId: body.basketId, basket: body.basket, destination: body.destination };
}
